import type { ChartConfig } from "@/components/ui/ChartTooltip";

/** One spoke row: categorical `angleKey`, one or more numeric series columns. */
export type RadarDatum = Record<string, string | number | undefined>;

const labelForKey = (key: string): string =>
  key.length > 0 ? `${key.charAt(0).toUpperCase()}${key.slice(1)}` : key;

/** Same palette as pie/horizontal bar — theme tokens only (no hex in specs). */
const RADAR_CHART_SEQUENCE = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
] as const;

/**
 * For overlaid radar lines, skip adjacent slots so hues differ more than chart-1 vs chart-2
 * often do in the same theme.
 */
const MULTI_SERIES_CHART_SLOTS = [0, 2, 4, 1, 3] as const;

const chartColorAtIndex = (idx: number): string =>
  RADAR_CHART_SEQUENCE[idx % RADAR_CHART_SEQUENCE.length] ??
  RADAR_CHART_SEQUENCE[0];

/**
 * Maps series key to stroke/fill: sentiment CSS vars when the key matches, else
 * {@link RADAR_CHART_SEQUENCE} by `paletteIndex`.
 */
const colorForSeriesKey = (key: string, paletteIndex: number): string => {
  const n = key.toLowerCase();
  if (n.includes("positive")) return "var(--sentiment-positive)";
  if (n.includes("negative")) return "var(--sentiment-negative)";
  if (n.includes("neutral")) return "var(--sentiment-neutral)";
  return chartColorAtIndex(paletteIndex);
};

/**
 * Multi-series radar: sentiment keys unchanged; otherwise pick non-adjacent palette slots
 * so overlaid polygons read as clearly different colors.
 */
const colorForMultiSeriesKey = (key: string, seriesIndex: number): string => {
  const n = key.toLowerCase();
  if (n.includes("positive")) return "var(--sentiment-positive)";
  if (n.includes("negative")) return "var(--sentiment-negative)";
  if (n.includes("neutral")) return "var(--sentiment-neutral)";
  const slot =
    MULTI_SERIES_CHART_SLOTS[seriesIndex % MULTI_SERIES_CHART_SLOTS.length] ??
    0;
  return chartColorAtIndex(slot);
};

/**
 * Builds {@link ChartConfig} and chart rows for {@link RadarChartView} (e.g. from Storybook).
 * Preserves input order of valid rows. Adds per-angle entries for tooltip labels on single-series charts.
 */
export const buildRadarChartModel = (input: {
  data: Array<Record<string, unknown>>;
  angleKey: string;
  valueKey: string;
  color?: string | null;
}): { chartConfig: ChartConfig; chartData: RadarDatum[] } => {
  const { data, angleKey, valueKey, color } = input;

  const chartData: RadarDatum[] = [];

  for (const row of data) {
    const angleRaw = row[angleKey];
    const angle =
      typeof angleRaw === "string" ? angleRaw : String(angleRaw ?? "");
    const valRaw = row[valueKey];
    const num =
      typeof valRaw === "number"
        ? valRaw
        : typeof valRaw === "string"
          ? Number.parseFloat(valRaw)
          : Number(valRaw);
    if (angle === "" || Number.isNaN(num)) continue;

    chartData.push({
      ...row,
      [angleKey]: angle,
      [valueKey]: num,
    } as RadarDatum);
  }

  const chartConfig: ChartConfig = {
    [valueKey]: {
      label: labelForKey(valueKey),
      color: color ?? colorForSeriesKey(valueKey, 0),
    },
  };

  for (const row of chartData) {
    const a = String(row[angleKey] ?? "");
    if (a !== "" && chartConfig[a] === undefined) {
      chartConfig[a] = { label: a };
    }
  }

  return { chartConfig, chartData };
};

/**
 * Parses a cell to a number; missing or non-numeric values become `0` so sparse LLM/tool rows
 * still render a spoke instead of dropping the whole row.
 */
const parseSeriesCell = (raw: unknown): number => {
  const num =
    typeof raw === "number"
      ? raw
      : typeof raw === "string"
        ? Number.parseFloat(raw)
        : Number(raw);
  return Number.isNaN(num) ? 0 : num;
};

/**
 * Builds {@link ChartConfig} and rows for multi-series radar (overlaid {@link Radar} lines).
 * Each `series` entry is a numeric column in `data`. Series keys named positive / negative / neutral
 * (case-insensitive) get semantic colors; others use the chart palette by index.
 * Missing or invalid numeric cells use `0` so partial rows are kept (sparse columns from LLM output).
 */
export const buildRadarChartMultiModel = (input: {
  data: Array<Record<string, unknown>>;
  angleKey: string;
  series: { key: string; label: string; color?: string | null }[];
}): { chartConfig: ChartConfig; chartData: RadarDatum[] } => {
  const { data, angleKey, series } = input;
  const chartData: RadarDatum[] = [];

  for (const row of data) {
    const angleRaw = row[angleKey];
    const angle =
      typeof angleRaw === "string" ? angleRaw : String(angleRaw ?? "");
    if (angle === "") continue;

    const normalized = { ...row, [angleKey]: angle } as RadarDatum;
    for (const s of series) {
      normalized[s.key] = parseSeriesCell(row[s.key]);
    }

    chartData.push(normalized as RadarDatum);
  }

  const chartConfig: ChartConfig = {};
  for (let i = 0; i < series.length; i++) {
    const s = series[i];
    if (!s) continue;
    chartConfig[s.key] = {
      label: s.label,
      color: s.color ?? colorForMultiSeriesKey(s.key, i),
    };
  }

  for (const row of chartData) {
    const a = String(row[angleKey] ?? "");
    if (a !== "" && chartConfig[a] === undefined) {
      chartConfig[a] = { label: a };
    }
  }

  return { chartConfig, chartData };
};

/**
 * Catalog adapter: single series (`dataKey`) or multi (`dataKeys`). `nameKey` is the axis dimension field.
 */
export const buildRadarChartCatalogModel = (input: {
  data: Array<Record<string, unknown>>;
  nameKey: string;
  dataKey: string | null;
  dataKeys: Array<{ key: string; label: string; color?: string | null }> | null;
  color?: string | null;
}):
  | {
      mode: "single";
      chartConfig: ChartConfig;
      chartData: RadarDatum[];
      angleKey: string;
      dataKey: string;
    }
  | {
      mode: "multi";
      chartConfig: ChartConfig;
      chartData: RadarDatum[];
      angleKey: string;
      dataKeys: { key: string; label: string; color?: string | null }[];
    } => {
  const { data, nameKey, dataKey, dataKeys, color } = input;
  const angleKey = nameKey;

  const hasMulti = dataKeys != null && dataKeys.length > 0;
  const hasSingle = dataKey != null && dataKey !== "";

  if (hasMulti) {
    const { chartConfig, chartData } = buildRadarChartMultiModel({
      data,
      angleKey,
      series: dataKeys,
    });
    return {
      mode: "multi",
      chartConfig,
      chartData,
      angleKey,
      dataKeys: dataKeys,
    };
  }

  if (hasSingle) {
    const { chartConfig, chartData } = buildRadarChartModel({
      data,
      angleKey,
      valueKey: dataKey,
      color,
    });
    return {
      mode: "single",
      chartConfig,
      chartData,
      angleKey,
      dataKey,
    };
  }

  throw new Error(
    "buildRadarChartCatalogModel: specify dataKey or non-empty dataKeys",
  );
};
